import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';

// Vista informativa: muestra a la empresa el ÚNICO link público que debe
// compartir con sus clientes. Ese link los lleva a un portal con barra de
// navegación que reúne todas las secciones activas (tienda, citas, PQR y
// noticias). Debajo, la empresa puede activar o desactivar cada sección: solo
// las activas aparecerán dentro del portal.
function MisLinks({ token }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(null);
  const [qrCopied, setQrCopied] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [toggleError, setToggleError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/pay/company-info', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setInfo(d))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));

    // QR del mismo link: lo genera el servidor y lo deja publicado en /media/qr,
    // así la empresa puede verlo, descargarlo o compartir la URL del PNG.
    fetch('/api/pay/company-qr', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setQr(d))
      .catch(() => setQr(null));
  }, [token]);

  const slug = info?.name ? info.name.replace(/ /g, '_') : '';
  const origin = window.location.origin;
  const portalUrl = slug ? `${origin}/p/${slug}` : '';

  // Secciones que el cliente verá dentro del portal. Cada una se puede activar o
  // desactivar aquí mismo (mismo endpoint que usa "Mi página web → Empresa").
  const SECTIONS = [
    { key: 'cita',   titulo: 'Citas',            emoji: '📅', activo: !!info?.cita_activa,    endpoint: 'company-cita',    field: 'cita_activa',    cta: { label: '➕ Agregar servicio', to: '/servicios' } },
    { key: 'tienda', titulo: 'Productos / Tienda', emoji: '🛍️', activo: !!info?.tienda_activa, endpoint: 'company-store',   field: 'tienda_activa',  cta: { label: '➕ Agregar producto', to: '/productos' } },
    { key: 'ticket', titulo: 'PQR / Tickets',     emoji: '🎫', activo: !!info?.tickets_activa, endpoint: 'company-tickets', field: 'tickets_activa', cta: { label: '🎫 Ver PQR de mis clientes', to: '/utilidades-tickets' } },
    { key: 'news',   titulo: 'Noticias',          emoji: '📰', activo: !!info?.news_activa,    endpoint: 'company-news',    field: 'news_activa',    cta: { label: '➕ Agregar noticia', to: '/utilidades-noticias' } },
    { key: 'canalweb', titulo: 'Canal Web (chat)', emoji: '💬', activo: !!info?.canal_web_activa, endpoint: 'company-canal-web', field: 'canal_web_activa', gated: true, cta: { label: '⚙️ Configurar canal web', to: '/canales' } },
  ];

  const activeCount = SECTIONS.filter(s => s.activo).length;

  // Activa o desactiva una sección y actualiza el estado local.
  const toggle = async (s) => {
    const next = !s.activo;
    setToggling(s.key);
    setToggleError(null);
    try {
      const res = await fetch(`/api/pay/${s.endpoint}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [s.field]: next }),
      });
      if (!res.ok) throw new Error('toggle failed');
      setInfo((prev) => ({ ...prev, [s.field]: next }));
    } catch {
      setToggleError(s.key);
    } finally {
      setToggling(null);
    }
  };

  const writeClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback para navegadores sin permiso de portapapeles.
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(el);
    }
  };

  const copy = async () => {
    await writeClipboard(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyQr = async () => {
    await writeClipboard(qr.url);
    setQrCopied(true);
    setTimeout(() => setQrCopied(false), 2000);
  };

  if (loading) {
    return (
      <main style={{ padding: '3rem 1.5rem', maxWidth: '760px', margin: '0 auto', textAlign: 'center', color: theme.colors.textMuted }}>
        Cargando tu link...
      </main>
    );
  }

  return (
    <main style={{ padding: '2.5rem 1.5rem', maxWidth: '760px', margin: '0 auto' }}>
      <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.5rem' }}>
        🔗 El link para mis clientes
      </h1>
      <div style={{
        backgroundColor: `${theme.colors.primary}11`,
        border: `1px solid ${theme.colors.primary}`,
        borderRadius: '12px',
        padding: '1rem 1.2rem',
        marginBottom: '1.8rem',
      }}>
        <p style={{ color: theme.colors.text, fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
          Ahora tienes <strong>un solo link</strong> para entregarle a tus clientes. Al abrirlo
          encontrarán una página con un menú arriba que reúne <strong>todas las secciones que
          tengas activas</strong> (citas, tienda, PQR y noticias) y podrán moverse entre ellas
          sin cambiar de pestaña. Cópialo y compártelo. 👇
        </p>
      </div>

      {!slug && (
        <p style={{ color: theme.colors.error, fontSize: '0.9rem' }}>
          No se pudo cargar el nombre de tu empresa. Intenta recargar la página.
        </p>
      )}

      {/* ─── Link único ─── */}
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `1px solid ${theme.colors.border}`,
        padding: '1.3rem',
        boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
        marginBottom: '2rem',
      }}>
        <a
          href={portalUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '8px',
            padding: '0.8rem 1rem',
            color: theme.colors.primary,
            fontSize: '1rem',
            fontWeight: 600,
            wordBreak: 'break-all',
            textDecoration: 'none',
            marginBottom: '0.9rem',
          }}
        >
          {portalUrl || '—'}
        </a>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button
            onClick={copy}
            disabled={!portalUrl}
            style={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.background,
              border: 'none', borderRadius: '8px',
              padding: '0.6rem 1.4rem', fontSize: '0.9rem', fontWeight: 700,
              cursor: portalUrl ? 'pointer' : 'default', opacity: portalUrl ? 1 : 0.6,
              fontFamily: 'inherit',
            }}
          >
            {copied ? '✓ Copiado' : '📋 Copiar link'}
          </button>
          <a
            href={portalUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: 'transparent',
              color: theme.colors.primary,
              border: `1px solid ${theme.colors.primary}`,
              borderRadius: '8px',
              padding: '0.6rem 1.4rem', fontSize: '0.9rem', fontWeight: 700,
              cursor: 'pointer', textDecoration: 'none',
            }}
          >
            ↗ Abrir y ver cómo se ve
          </a>
        </div>
        {activeCount === 0 && (
          <p style={{ color: theme.colors.textMuted, fontSize: '0.82rem', margin: '0.9rem 0 0' }}>
            ⚠️ Aún no tienes ninguna sección activa, así que el link todavía no mostrará nada.
            Activa al menos una abajo. 👇
          </p>
        )}
      </div>

      {/* ─── QR del link ─── */}
      {qr?.url && (
        <div style={{
          backgroundColor: theme.colors.surface,
          borderRadius: '14px',
          border: `1px solid ${theme.colors.border}`,
          padding: '1.3rem',
          boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
          marginBottom: '2rem',
          textAlign: 'center',
        }}>
          <h2 style={{ color: theme.colors.text, fontSize: '1.1rem', margin: '0 0 0.4rem' }}>
            📱 El QR de ese mismo link
          </h2>
          <p style={{ color: theme.colors.textMuted, fontSize: '0.88rem', lineHeight: 1.6, margin: '0 0 1.1rem' }}>
            Tus clientes lo apuntan con la cámara del celular y llegan al mismo portal.
            <strong> Haz clic en el QR para descargarlo</strong> e imprimirlo, pegarlo en la
            vitrina o ponerlo en tus publicaciones.
          </p>

          <a
            href={qr.url}
            download={`qr-${slug || 'mi-negocio'}.png`}
            title="Clic para descargar el QR"
            style={{
              display: 'inline-block',
              backgroundColor: '#FFFFFF',
              padding: '10px',
              borderRadius: '12px',
              border: `1px solid ${theme.colors.border}`,
              lineHeight: 0,
              cursor: 'pointer',
            }}
          >
            <img
              src={qr.url}
              alt="Código QR del link para tus clientes"
              width={200}
              height={200}
              style={{ display: 'block', width: '200px', height: '200px' }}
            />
          </a>

          <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '0.7rem 0 1.1rem' }}>
            ⬇️ Clic en la imagen para guardarla en tu computador
          </p>

          <p style={{ color: theme.colors.textMuted, fontSize: '0.82rem', margin: '0 0 0.5rem' }}>
            O comparte directamente la dirección del QR:
          </p>
          <a
            href={qr.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              backgroundColor: theme.colors.background,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              padding: '0.7rem 1rem',
              color: theme.colors.primary,
              fontSize: '0.85rem',
              fontWeight: 600,
              wordBreak: 'break-all',
              textDecoration: 'none',
              marginBottom: '0.8rem',
            }}
          >
            {qr.url}
          </a>
          <button
            onClick={copyQr}
            style={{
              backgroundColor: 'transparent',
              color: theme.colors.primary,
              border: `1px solid ${theme.colors.primary}`,
              borderRadius: '8px',
              padding: '0.55rem 1.3rem', fontSize: '0.86rem', fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {qrCopied ? '✓ Copiada' : '📋 Copiar dirección del QR'}
          </button>
        </div>
      )}

      {/* ─── Qué secciones aparecen en el link ─── */}
      <h2 style={{ color: theme.colors.text, fontSize: '1.1rem', margin: '0 0 0.4rem' }}>
        ¿Qué secciones aparecen en tu link?
      </h2>
      <p style={{ color: theme.colors.textMuted, fontSize: '0.88rem', lineHeight: 1.6, margin: '0 0 1.2rem' }}>
        Activa o desactiva cada sección. Solo las activas aparecerán en el menú del portal que ven tus clientes.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {SECTIONS.map((s) => (
          <div key={s.key} style={{
            backgroundColor: theme.colors.surface,
            borderRadius: '14px',
            border: `1px solid ${theme.colors.border}`,
            padding: '1.1rem 1.3rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap' }}>
              <h3 style={{ color: theme.colors.primary, fontSize: '1.05rem', margin: 0 }}>
                {s.emoji} {s.titulo}
              </h3>
              {s.activo ? (
                <span style={{ color: theme.colors.success, fontSize: '0.8rem', fontWeight: 700 }}>✓ Activa</span>
              ) : (
                <span style={{ color: theme.colors.textMuted, fontSize: '0.8rem', fontWeight: 600 }}>Desactivada</span>
              )}
            </div>

            {s.gated && !info?.has_web_channel && (
              <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', margin: '0.6rem 0 0' }}>
                ⚠️ Para activarlo primero debes crear un <strong>canal web</strong> en Configuración &gt; Canales.
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
              <button
                onClick={() => toggle(s)}
                disabled={toggling === s.key || (s.gated && !s.activo && !info?.has_web_channel)}
                style={{
                  backgroundColor: s.activo ? 'transparent' : theme.colors.success,
                  color: s.activo ? theme.colors.error : theme.colors.background,
                  border: s.activo ? `1px solid ${theme.colors.error}` : 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1.1rem', fontSize: '0.84rem', fontWeight: 700,
                  cursor: (toggling === s.key || (s.gated && !s.activo && !info?.has_web_channel)) ? 'default' : 'pointer',
                  opacity: (toggling === s.key || (s.gated && !s.activo && !info?.has_web_channel)) ? 0.5 : 1,
                  fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}
              >
                {toggling === s.key
                  ? (s.activo ? 'Desactivando...' : 'Activando...')
                  : (s.activo ? 'Desactivar' : '✓ Activar')}
              </button>
              <button
                onClick={() => navigate(s.cta.to)}
                style={{
                  backgroundColor: theme.colors.surface,
                  color: theme.colors.text,
                  border: `1px solid ${theme.colors.primary}`,
                  borderRadius: '8px',
                  padding: '0.5rem 1.1rem', fontSize: '0.84rem', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {s.cta.label}
              </button>
            </div>

            {toggleError === s.key && (
              <p style={{ color: theme.colors.error, fontSize: '0.78rem', margin: '0.5rem 0 0' }}>
                No se pudo cambiar el estado. Intenta de nuevo.
              </p>
            )}
          </div>
        ))}
      </div>
    </main>
  );
}

export default MisLinks;
